import { type ChainAvailabilityItem } from '@/models/appointment-chain-availability.ts';
import { type AppointmentChainConfig } from '@/models/appointment-chain-config.ts';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowForward from '@material-ui/icons/ArrowForward';
import CheckCircle from '@material-ui/icons/CheckCircle';
import { useTranslation } from 'react-i18next';

import { titleAfterId } from '@/utils/appointment-card-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.js';

import ChainCounts from './chain-counts.tsx';
import ChainMetaIcon from './chain-meta-icon.tsx';
import { countDistinctDays, dayKey, resolveChainName } from './chain-utils.ts';

/**
 * Presentational only: the availability page, chain config, and `resolveStepLabel` lookup are injected by
 * the parent, keeping this component free of store/data-fetch coupling and testable with plain fixtures.
 */

const BADGE_SIZE = 28;
// Banner tints are derived from the brand green (`theme.palette.common.green`) rather than hardcoded.
const GREEN_TINT_BG_OPACITY = 0.08;
const GREEN_TINT_BORDER_OPACITY = 0.35;

const useStyles = makeStyles((theme) => ({
    banner: {
        backgroundColor: alpha(theme.palette.common.green, GREEN_TINT_BG_OPACITY),
        border: `1px solid ${alpha(theme.palette.common.green, GREEN_TINT_BORDER_OPACITY)}`,
        borderRadius: theme.radius.default,
        padding: 20,
        boxSizing: 'border-box',
        marginBottom: 20,
    },
    bannerHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    bannerIcon: {
        color: theme.palette.common.green,
    },
    eyebrow: {
        fontWeight: theme.weight.heading,
        fontSize: '0.75rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.palette.common.green,
    },
    chainName: {
        ...theme.typography.HL3,
        marginTop: 8,
    },
    divider: {
        border: 'none',
        borderTop: `1px solid ${alpha(theme.palette.common.green, GREEN_TINT_BORDER_OPACITY)}`,
        margin: '16px 0',
    },
    helperRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: '0.875rem',
        color: theme.palette.common.midGrey,
    },
    tableCard: {
        backgroundColor: 'white',
        maxWidth: theme.layout.contentWidth,
        border: `1px solid ${theme.palette.common.inputDisabledGray}`,
        borderRadius: theme.radius.default,
        boxSizing: 'border-box',
        // Rows sit flush to the card border; hidden clips the square table corners to the rounded card.
        overflow: 'hidden',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        // The card border is the outer bottom rule, so the last row drops its own to avoid a doubled line.
        '& tbody tr:last-child td': {
            borderBottom: 'none',
        },
    },
    headerCell: {
        textAlign: 'left',
        fontWeight: theme.weight.heading,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.common.midGrey,
        padding: '12px 16px',
        // Header row keeps only the bottom rule (no vertical dividers); the body cells carry the grid lines.
        borderBottom: `1px solid ${theme.palette.common.inputDisabledGray}`,
        [theme.breakpoints.down('xs')]: {
            padding: '12px 8px',
            fontSize: '0.6875rem',
        },
    },
    cell: {
        textAlign: 'left',
        verticalAlign: 'top',
        fontSize: '0.9375rem',
        color: theme.palette.common.darkGrey,
        padding: '14px 16px',
        borderBottom: `1px solid ${theme.palette.common.inputDisabledGray}`,
        // Vertical column dividers on the body rows; the index (#) and trailing edges carry no divider.
        borderRight: `1px solid ${theme.palette.common.inputDisabledGray}`,
        '&:first-child': {
            borderRight: 'none',
        },
        '&:last-child': {
            borderRight: 'none',
        },
        [theme.breakpoints.down('xs')]: {
            padding: '12px 8px',
            fontSize: '0.8125rem',
        },
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: BADGE_SIZE,
        height: BADGE_SIZE,
        borderRadius: '50%',
        // Brand colour with auto-contrast text, matching the appointments series/collection chip
        // (`appointment-collection-item.tsx`) — `common.yellow` is the customer's primary colour.
        backgroundColor: theme.palette.common.yellow,
        color: theme.palette.getContrastText(theme.palette.common.yellow),
        fontWeight: theme.weight.heading,
        fontSize: '0.875rem',
    },
    stepName: {
        fontWeight: theme.weight.heading,
        fontSize: '0.9375rem',
        color: theme.palette.common.darkGrey,
        // Break long treatment names so the step column shrinks to fit the mobile viewport instead of scrolling.
        overflowWrap: 'anywhere',
        [theme.breakpoints.down('xs')]: {
            fontSize: '0.8125rem',
        },
    },
    foldedMeta: {
        fontSize: '0.8125rem',
        color: theme.palette.common.midGrey,
        marginTop: 4,
        [theme.breakpoints.down('xs')]: {
            fontSize: '0.75rem',
        },
    },
}));

interface Props {
    items: ChainAvailabilityItem[];
    selectedChainConfig: AppointmentChainConfig;
    resolveStepLabel: (appointmentTypeKey: string) => string;
    // The overview card supplies its own heading + counts, so it hides the suggestions banner.
    showBanner?: boolean;
}

/**
 * Per-row span metadata for the date column: the first row of a same-day run carries the run length so its
 * `Datum` cell can `rowSpan` over the following same-day rows, which omit the cell entirely. The run is
 * computed over *consecutive* rows (the BE returns steps in order), so the row-span holds at every breakpoint.
 */
interface DateSpan {
    render: boolean;
    rowSpan: number;
}

const buildDateSpans = (items: ChainAvailabilityItem[]): DateSpan[] => {
    const spans: DateSpan[] = items.map(() => ({ render: false, rowSpan: 1 }));

    for (let index = 0; index < items.length; index += 1) {
        if (index > 0 && dayKey(items[index].start_date) === dayKey(items[index - 1].start_date)) {
            continue;
        }
        let runLength = 1;
        while (
            index + runLength < items.length &&
            dayKey(items[index + runLength].start_date) === dayKey(items[index].start_date)
        ) {
            runLength += 1;
        }
        spans[index] = { render: true, rowSpan: runLength };
    }

    return spans;
};

/**
 * Resolves a suggestion's `calendar_id` to a display name — a person for the main step, a role for
 * non-main steps.
 */
const resolvePractitionerName = (config: AppointmentChainConfig, calendarId: string, stepOrder: number): string => {
    const step = config.steps.find((s) => s.order === stepOrder);
    const inStep = step?.eligible_practitioners.find((practitioner) =>
        practitioner.calendar_pms_ids.includes(calendarId)
    );
    if (inStep) return inStep.display_name;

    for (const candidateStep of config.steps) {
        const match = candidateStep.eligible_practitioners.find((practitioner) =>
            practitioner.calendar_pms_ids.includes(calendarId)
        );
        if (match) return match.display_name;
    }
    return '';
};

const durationInMinutes = (item: ChainAvailabilityItem): number =>
    dayjs(item.end_date).diff(dayjs(item.start_date), 'minute');

const ChainSuggestionsTable = ({ items, selectedChainConfig, resolveStepLabel, showBanner = true }: Props) => {
    const classes = useStyles();
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const chainName = resolveChainName(selectedChainConfig, i18n.language);
    const stepCount = selectedChainConfig.steps.length;
    const appointmentCount = countDistinctDays(items);
    const dateSpans = buildDateSpans(items);

    return (
        <div data-testid="chain-suggestions-table">
            {showBanner && (
                <div className={classes.banner}>
                    <div className={classes.bannerHeader}>
                        <CheckCircle className={classes.bannerIcon} aria-hidden="true" />
                        <Typography className={classes.eyebrow}>{t('ChainSuggestionsEyebrow')}</Typography>
                    </div>
                    <Typography className={classes.chainName}>{chainName}</Typography>
                    <ChainCounts stepCount={stepCount} appointmentCount={appointmentCount} />
                    <hr className={classes.divider} />
                    <div className={classes.helperRow}>
                        <ChainMetaIcon icon={ArrowForward} />
                        <Typography variant="inherit" component="span">
                            {t('ChainSuggestionsHelper')}
                        </Typography>
                    </div>
                </div>
            )}

            <TableContainer className={classes.tableCard}>
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell scope="col" className={classes.headerCell}>
                                <Typography variant="inherit" component="span">
                                    {t('ChainColIndex')}
                                </Typography>
                            </TableCell>
                            <TableCell scope="col" className={classes.headerCell}>
                                <Typography variant="inherit" component="span">
                                    {t('ChainColStep')}
                                </Typography>
                            </TableCell>
                            <TableCell scope="col" className={classes.headerCell}>
                                <Typography variant="inherit" component="span">
                                    {t('ChainColDate')}
                                </Typography>
                            </TableCell>
                            <TableCell scope="col" className={classes.headerCell}>
                                <Typography variant="inherit" component="span">
                                    {t('ChainColTime')}
                                </Typography>
                            </TableCell>
                            {!isMobile && (
                                <>
                                    <TableCell scope="col" className={classes.headerCell}>
                                        <Typography variant="inherit" component="span">
                                            {t('ChainColDuration')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell scope="col" className={classes.headerCell}>
                                        <Typography variant="inherit" component="span">
                                            {t('ChainColPractitioner')}
                                        </Typography>
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => {
                            const stepLabel = titleAfterId(resolveStepLabel(item.appointment_type_key));
                            const practitioner = resolvePractitionerName(
                                selectedChainConfig,
                                item.calendar_id,
                                item.step_order
                            );
                            const duration = t('ChainDurationMinutes', { count: durationInMinutes(item) });
                            const dateSpan = dateSpans[index];

                            return (
                                <TableRow key={`${item.step_order}-${item.calendar_id}`}>
                                    <TableCell className={classes.cell}>
                                        <Typography
                                            variant="inherit"
                                            component="span"
                                            className={classes.badge}
                                            aria-label={`${t('ChainColStep')} ${index + 1}`}
                                        >
                                            {index + 1}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        <Typography className={classes.stepName}>{stepLabel}</Typography>
                                        {isMobile && (
                                            <Typography className={classes.foldedMeta}>
                                                {practitioner ? `${duration} · ${practitioner}` : duration}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    {dateSpan.render && (
                                        <TableCell className={classes.cell} rowSpan={dateSpan.rowSpan}>
                                            <Typography variant="inherit" component="span">
                                                {dayjs(item.start_date).format('DD.MM.YYYY')}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell className={classes.cell}>
                                        <Typography variant="inherit" component="span">
                                            {dayjs(item.start_date).format('HH:mm')}
                                        </Typography>
                                    </TableCell>
                                    {!isMobile && (
                                        <>
                                            <TableCell className={classes.cell}>
                                                <Typography variant="inherit" component="span">
                                                    {duration}
                                                </Typography>
                                            </TableCell>
                                            <TableCell className={classes.cell}>
                                                <Typography variant="inherit" component="span">
                                                    {practitioner}
                                                </Typography>
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ChainSuggestionsTable;
